import React, { useState, useEffect } from "react";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  FaChevronDown,
  FaChevronUp,
  FaCopy,
  FaCode,
  FaInfoCircle,
  FaLightbulb,
} from "react-icons/fa";
import { useTheme } from "../context/ThemeContext";
import "../styles/CheatSheetContent.css";
import { ColorPsychologyGrid, ColorTheoryWheel } from "./ColorTheoryDiagrams";
import UXProcessDiagram from "./UXProcessDiagram";

// Helper function to create valid HTML IDs
const createSectionId = (text) => {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
    .replace(/^-+|-+$/g, "") // Remove leading/trailing hyphens
    .replace(/\s+/g, "-"); // Replace spaces with hyphens
};

const CheatSheetContent = ({ content }) => {
  const { isDark } = useTheme();
  const [expandedSections, setExpandedSections] = useState(() => {
    // Initialize with the first section expanded
    const initialExpanded = new Set([0]);

    // If there's a hash in the URL, find and expand the corresponding section
    const hash = window.location.hash.slice(1);
    if (hash && content.sections) {
      content.sections.forEach((section, index) => {
        const sectionId = createSectionId(section.title);
        if (hash === sectionId || hash.startsWith(`${sectionId}-`)) {
          initialExpanded.add(index);
        }
      });
    }
    return initialExpanded;
  });
  const [copiedIndex, setCopiedIndex] = useState(null);

  // Add effect to handle hash changes
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash && content.sections) {
        content.sections.forEach((section, index) => {
          const sectionId = createSectionId(section.title);
          if (hash === sectionId || hash.startsWith(`${sectionId}-`)) {
            setExpandedSections((prev) => new Set([...prev, index]));
          }
        });
      }
    };

    // Handle initial hash
    handleHashChange();

    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [content.sections]);

  const getLanguageFromCode = (code) => {
    if (code.includes("<div") || code.includes("<span")) return "html";
    if (code.includes("function") || code.includes("const"))
      return "javascript";
    if (code.includes("class") || code.includes("def")) return "python";
    return content.language || "javascript";
  };

  const toggleSection = (index) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSections(newExpanded);
  };

  const copyToClipboard = async (code, index) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error("Failed to copy code:", err);
    }
  };

  const renderComponent = (componentName) => {
    switch (componentName) {
      case "ColorPsychologyGrid":
        return <ColorPsychologyGrid />;
      case "ColorTheoryWheel":
        return <ColorTheoryWheel />;
      case "UXProcessDiagram":
        return <UXProcessDiagram />;
      default:
        return null;
    }
  };

  if (!content || !content.sections || !Array.isArray(content.sections)) {
    return (
      <div className="error-container">
        <h2 className="error-message">Invalid content structure</h2>
      </div>
    );
  }

  return (
    <div className={`cheatsheet-content ${isDark ? "dark" : "light"}`}>
      <div className="cheatsheet-header">
        <h1>{content.title}</h1>
        <div className="header-meta">
          <span className="level">{content.level}</span>
          {content.category && (
            <span className="category">{content.category}</span>
          )}
        </div>
        {content.description && (
          <p className="description">{content.description}</p>
        )}
      </div>

      <div className="sections">
        {content.sections.map((section, sectionIndex) => {
          if (!section || !section.title) {
            console.warn("Invalid section:", section);
            return null;
          }

          const sectionItems = section.items || section.content || [];
          const sectionId = createSectionId(section.title);

          return (
            <section
              key={sectionIndex}
              id={sectionId}
              className="content-section"
              data-section-title={section.title}
              data-section-id={sectionId}
            >
              <div
                className="section-header"
                onClick={() => toggleSection(sectionIndex)}
              >
                <div className="section-title">
                  <h2>{section.title}</h2>
                </div>
                {expandedSections.has(sectionIndex) ? (
                  <FaChevronUp className="chevron-icon" />
                ) : (
                  <FaChevronDown className="chevron-icon" />
                )}
              </div>

              {expandedSections.has(sectionIndex) && (
                <div className="section-content">
                  {sectionItems.map((item, itemIndex) => {
                    if (!item || typeof item !== "object") {
                      console.warn("Invalid item:", item);
                      return null;
                    }

                    const uniqueIndex = `${sectionIndex}-${itemIndex}`;
                    const itemId = item.title
                      ? createSectionId(item.title)
                      : `${sectionId}-item-${itemIndex}`;

                    return (
                      <article
                        key={itemIndex}
                        id={itemId}
                        className="content-item"
                        data-item-title={item.title}
                        data-item-id={itemId}
                        data-section-id={sectionId}
                      >
                        <div className="item-header">
                          <h3>
                            <FaCode className="code-icon" />
                            {item.title}
                          </h3>
                          {(item.content || item.description) && (
                            <p className="item-description">
                              {item.content || item.description}
                            </p>
                          )}
                          {(item.note || item.notes) && (
                            <div className="item-notes">
                              <FaInfoCircle className="icon" />
                              <span>{item.note || item.notes}</span>
                            </div>
                          )}
                        </div>

                        {item.component && renderComponent(item.component)}

                        {item.code && (
                          <div className="code-block">
                            <SyntaxHighlighter
                              language={getLanguageFromCode(item.code)}
                              style={vscDarkPlus}
                              customStyle={{
                                margin: 0,
                                borderRadius: "4px",
                                padding: "1rem",
                                fontSize: "14px",
                                backgroundColor: isDark ? "#1e1e1e" : "#f8f8f8",
                              }}
                              showLineNumbers={true}
                              wrapLines={true}
                              wrapLongLines={true}
                            >
                              {item.code}
                            </SyntaxHighlighter>
                            <button
                              className={`copy-button ${
                                copiedIndex === uniqueIndex ? "copied" : ""
                              }`}
                              onClick={() =>
                                copyToClipboard(item.code, uniqueIndex)
                              }
                            >
                              <FaCopy />
                              {copiedIndex === uniqueIndex ? "Copied!" : "Copy"}
                            </button>
                          </div>
                        )}

                        {item.examples && (
                          <div className="examples">
                            {item.examples.map((example, exampleIndex) => (
                              <div key={exampleIndex} className="example">
                                <h4>{example.title}</h4>
                                <p>{example.description}</p>
                                {example.code && (
                                  <div className="code-block">
                                    <SyntaxHighlighter
                                      language={
                                        content.language || "javascript"
                                      }
                                      style={vscDarkPlus}
                                      customStyle={{
                                        margin: 0,
                                        borderRadius: "4px",
                                        padding: "1rem",
                                        fontSize: "14px",
                                        backgroundColor: isDark
                                          ? "#1e1e1e"
                                          : "#f8f8f8",
                                      }}
                                      showLineNumbers={true}
                                      wrapLines={true}
                                      wrapLongLines={true}
                                    >
                                      {example.code}
                                    </SyntaxHighlighter>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </article>
                    );
                  })}
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default CheatSheetContent;
